import type { ParagraphEntry, ParagraphIndex } from './indexParagraphs';
import { getIsEmptyCastParagraph } from '../helpers/getIsEmptyCastParagraph';

/**
 * Blank paragraphs don't consume neighbor slots (see below), but a run longer
 * than this reads as a deliberate visual break — expansion stops at it rather
 * than dragging in content from the other side.
 */
const BLANK_RUN_LIMIT = 3;

/**
 * Returns the matched entry plus up to `expandCount` non-blank paragraphs on
 * each side, scoped to the same section and stopping at table boundaries.
 *
 * Blank paragraphs (no text) are spacing, not content: they don't consume a
 * neighbor slot, so a document that puts a blank line between paragraphs still
 * expands to real prose. A blank between the entry and a picked neighbor rides
 * along with it; blanks past the last picked neighbor are left out.
 */
export function expandNeighbors(
  entry: ParagraphEntry,
  index: ParagraphIndex,
  expandCount: number
): ParagraphEntry[] {
  if (expandCount <= 0) return [entry];

  return [
    entry,
    ...collectSide(entry, index, expandCount, -1),
    ...collectSide(entry, index, expandCount, 1),
  ];
}

function collectSide(
  entry: ParagraphEntry,
  index: ParagraphIndex,
  expandCount: number,
  direction: -1 | 1
): ParagraphEntry[] {
  const ordered = index.ordered;
  const start = entry.documentOrder;

  const picked: ParagraphEntry[] = [];
  let pendingBlanks: ParagraphEntry[] = [];
  let blankRun = 0;

  for (let offset = 1, slotsUsed = 0; slotsUsed < expandCount; offset++) {
    const candidate = ordered[start + direction * offset];
    if (!candidate) break;
    if (candidate.section !== entry.section) break;

    const isNeighbor = isSameNeighborhood(candidate, entry);
    if (isNeighbor && getIsEmptyCastParagraph(candidate.paragraph)) {
      blankRun += 1;
      if (blankRun > BLANK_RUN_LIMIT) break;
      pendingBlanks.push(candidate);
      continue;
    }
    blankRun = 0;

    /**
     * Non-neighborhood paragraphs (table-cell content beside the entry) are
     * never included but still use up a slot, bounding how far expansion can
     * jump past a table.
     */
    slotsUsed += 1;
    if (isNeighbor) {
      picked.push(...pendingBlanks, candidate);
      pendingBlanks = [];
    }
  }

  return picked;
}

/**
 * Two paragraphs are in the same neighborhood when they share both a section
 * AND a containing cell (or both have no cell). Crossing a table boundary
 * stops neighbor expansion — it doesn't make sense to drag in a table cell
 * just because it happens to sit next to the match in document order.
 */
function isSameNeighborhood(a: ParagraphEntry, b: ParagraphEntry): boolean {
  if (a.section !== b.section) return false;
  const aCell = innermostCell(a);
  const bCell = innermostCell(b);
  return aCell === bCell;
}

function innermostCell(entry: ParagraphEntry): unknown {
  for (let i = entry.ancestors.length - 1; i >= 0; i--) {
    if (entry.ancestors[i].type === 'tableCell') return entry.ancestors[i];
  }
  return null;
}
